import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HERO_SLIDES } from "../lib/siteData";

export const HeroSlider = () => {
  const [index, setIndex] = useState(0);
  const count = HERO_SLIDES.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);

  useEffect(() => {
    const id = setInterval(next, 7000);
    return () => clearInterval(id);
  }, [next]);

  const slide = HERO_SLIDES[index];

  return (
    <section
      data-testid="hero-slider"
      className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-[#1A1D20]"
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center ken-burns"
            style={{ backgroundImage: `url('${slide.url}')` }}
            aria-hidden="true"
          />
          {/* Gradient overlay for legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1D20]/85 via-[#1A1D20]/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D20]/70 via-transparent to-[#1A1D20]/30" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex items-end md:items-center pb-24 md:pb-0">
        <motion.div
          key={`content-${index}`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl text-[#FCFCFA]"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="hairline-gold" />
            <span
              className="uppercase tracking-[0.28em] text-[11px] md:text-xs font-semibold text-[#D4AF37]"
              data-testid="hero-overline"
            >
              {slide.overline}
            </span>
          </div>
          <h1
            className="font-serif-display text-4xl sm:text-5xl lg:text-7xl leading-[1.02] whitespace-pre-line tracking-tight"
            data-testid="hero-title"
          >
            {slide.title}
          </h1>
          <p className="mt-8 max-w-xl text-[15px] md:text-[17px] leading-relaxed text-white/85">
            {slide.body}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/investor-relations"
              data-testid="hero-cta-invest"
              className="inline-flex items-center bg-[#D4AF37] text-[#1A1D20] hover:bg-[#FCFCFA] transition-colors duration-300 px-8 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold"
            >
              Partner With Us
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="inline-flex items-center border border-white/60 text-white hover:bg-white hover:text-[#1A1D20] transition-colors duration-300 px-8 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold"
            >
              Our Services
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-8 right-6 md:right-12 z-20 flex items-center gap-3">
        <button
          onClick={prev}
          aria-label="Previous slide"
          data-testid="hero-prev"
          className="w-11 h-11 border border-white/40 text-white hover:bg-[#D4AF37] hover:text-[#1A1D20] hover:border-[#D4AF37] transition-colors flex items-center justify-center"
        >
          <ChevronLeft size={18} strokeWidth={1.5} />
        </button>
        <button
          onClick={next}
          aria-label="Next slide"
          data-testid="hero-next"
          className="w-11 h-11 border border-white/40 text-white hover:bg-[#D4AF37] hover:text-[#1A1D20] hover:border-[#D4AF37] transition-colors flex items-center justify-center"
        >
          <ChevronRight size={18} strokeWidth={1.5} />
        </button>
      </div>

      {/* Indicators */}
      <div className="absolute bottom-10 left-6 md:left-12 z-20 flex items-center gap-4">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            data-testid={`hero-indicator-${i}`}
            className="group flex items-center gap-2"
            aria-label={`Go to slide ${i + 1}`}
          >
            <span className={`text-[11px] tracking-[0.25em] ${i === index ? "text-[#D4AF37]" : "text-white/50"}`}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <span
              className={`h-[1px] transition-all duration-500 ${
                i === index ? "bg-[#D4AF37] w-14" : "bg-white/40 w-6 group-hover:w-10"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
